import { useState } from 'react';
import { FaSort, FaTimes } from 'react-icons/fa';
import { MAX_SORT_KEYS, SortParam } from '../../types/pagination';

export interface SortFieldOption {
  value: string;
  label: string;
}

interface SortControlProps {
  keys: SortParam[];
  fields: SortFieldOption[];
  onChange: (keys: SortParam[]) => void;
  onReset: () => void;
}

/**
 * Visible sort panel: lists the sort keys in order (up to MAX_SORT_KEYS), each with a field, a direction and a
 * remove button, and an "Add sort" button for the next key. The backend applies the keys in this order.
 */
export const SortControl = ({ keys, fields, onChange, onReset }: SortControlProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const labelOf = (value: string) => fields.find((field) => field.value === value)?.label ?? value;
  const unusedFields = fields.filter((field) => !keys.some((key) => key.field === field.value));
  const canAdd = keys.length < MAX_SORT_KEYS && unusedFields.length > 0;

  const updateKey = (index: number, change: Partial<SortParam>) => {
    onChange(keys.map((key, i) => (i === index ? { ...key, ...change } : key)));
  };

  const removeKey = (index: number) => {
    onChange(keys.filter((_, i) => i !== index));
  };

  const addKey = () => {
    if (!canAdd) return;
    onChange([...keys, { field: unusedFields[0].value, direction: 'asc' }]);
  };

  return (
    <div className="relative">
      <button
        data-testid="sort-control-toggle"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-3.5 py-2 text-[13px] font-medium rounded-lg transition-all duration-150 ${
          isOpen
            ? 'bg-[#171717] text-white border border-[#171717]'
            : 'bg-white text-[#444] hover:bg-[#fafafa] border border-[#eaeaea]'
        }`}
      >
        <FaSort className="w-3 h-3" />
        <span>Sort</span>
        {keys.length > 0 && (
          <span className="px-1.5 py-0.5 text-[10px] bg-[#f5f5f5] text-[#444] rounded-full">{keys.length}</span>
        )}
      </button>

      {isOpen && (
        <div data-testid="sort-control-panel" className="absolute right-0 z-20 mt-2 w-80bg-white border border-[#eaeaea] rounded-xl shadow-lg p-3">
          <div className="text-[11px] text-[#888] mb-2">Sorted by, in order</div>
          {keys.length === 0 && <div className="text-xs text-[#888] mb-2">No sort applied.</div>}
          <div className="space-y-2">
            {keys.map((key, index) => (
              <div key={`${key.field}-${index}`} className="flex items-center gap-2">
                <span className="text-[11px] text-[#888] w-4">{index + 1}</span>
                <select
                  data-testid={`sort-control-field-${index}`}
                  value={key.field}
                  onChange={(e) => updateKey(index, { field: e.target.value })}
                  className="flex-1 px-2 py-1 text-xs border border-[#eaeaea] rounded-lg bg-white"
                >
                  {fields
                    .filter((field) => field.value === key.field || !keys.some((k) => k.field === field.value))
                    .map((field) => (
                      <option key={field.value} value={field.value}>{labelOf(field.value)}</option>
                    ))}
                </select>
                <select
                  data-testid={`sort-control-direction-${index}`}
                  value={key.direction}
                  onChange={(e) => updateKey(index, { direction: e.target.value as SortParam['direction'] })}
                  className="px-2 py-1 text-xs border border-[#eaeaea] rounded-lg bg-white"
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
                <button
                  data-testid={`sort-control-remove-${index}`}
                  onClick={() => removeKey(index)}
                  className="text-[#aaa] hover:text-[#171717] p-1"
                  title="Remove this sort"
                >
                  <FaTimes className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-3">
            <button
              data-testid="sort-control-add"
              onClick={addKey}
              disabled={!canAdd}
              className="text-xs font-medium text-[#444] hover:text-[#171717] disabled:opacity-40"
            >
              + Add sort {keys.length >= MAX_SORT_KEYS ? `(max ${MAX_SORT_KEYS})` : ''}
            </button>
            <button data-testid="sort-control-reset" onClick={onReset}className="text-xs text-[#888] hover:text-[#171717]">
              Reset to default
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
